/**
 * Tagesplan „Meine Aufgaben“ (Board B): reine Ableitungen aus der Tagesliste des Servers. Was eine
 * Reinigungskraft tun darf, sagen die Flags `canClaim` und `canMutate` — nie die Rolle im Client.
 */
import type { CleaningTaskQueueResponse } from '@/shared/api';

type Task = CleaningTaskQueueResponse;

/** `finish` = ohne Fotos mit einem Tipp erledigt melden (übernehmen, starten, abschließen). */
export type PlanStep = 'finish' | 'claim' | 'start' | 'continue';

export interface DayPlan {
  /** Die eigene laufende Aufgabe („Jetzt dran“); weitere laufende stehen in der Zeitleiste. */
  current: Task | null;
  /** Frei oder mir zugeteilt und noch offen, nach Beginn sortiert. */
  upcoming: Task[];
  /** Meine erledigten Aufgaben des Tages. */
  done: Task[];
  /**
   * Entfallen (Buchung weg), soweit sie mir gehörten oder frei waren: sichtbar statt spurlos weg,
   * denn sie standen womöglich schon auf dem Plan von heute früh.
   */
  cancelled: Task[];
  /** Aufgaben bei anderen: nur lesbar, damit niemand doppelt hinfährt. */
  others: Task[];
}

export const isFinalTask = (task: Pick<Task, 'status'>) =>
  task.status === 'Completed' || task.status === 'Issue' || task.status === 'Cancelled';

const byStart = (left: Task, right: Task) =>
  left.scheduledFromLocalTime.localeCompare(right.scheduledFromLocalTime) ||
  left.propertyName.localeCompare(right.propertyName, 'de');

export function buildDayPlan(tasks: Task[], userId: string | undefined): DayPlan {
  const mine = (task: Task) => task.assignedToUserId !== null && task.assignedToUserId === userId;
  const sorted = [...tasks].sort(byStart);
  const current = sorted.find((task) => mine(task) && task.status === 'InProgress') ?? null;
  const cancelled = (task: Task) => task.status === 'Cancelled';
  return {
    current,
    upcoming: sorted.filter(
      (task) => task !== current && !isFinalTask(task) && (task.canClaim || mine(task)),
    ),
    done: sorted.filter((task) => mine(task) && isFinalTask(task) && !cancelled(task)),
    cancelled: sorted.filter(
      (task) => cancelled(task) && (mine(task) || task.assignedToUserId === null),
    ),
    others: sorted.filter((task) => !mine(task) && !task.canClaim && !cancelled(task)),
  };
}

/** Der eine Knopf im Tagesplan; null, wenn der Server nichts erlaubt oder nichts mehr offen ist. */
export function nextStep(task: Task, userId: string | undefined): PlanStep | null {
  if (isFinalTask(task)) return null;
  const withoutPhotos = task.photoSlotCount === 0;
  if (task.canClaim) return withoutPhotos ? 'finish' : 'claim';
  if (!task.canMutate || task.assignedToUserId !== userId) return null;
  if (withoutPhotos) return 'finish';
  return task.status === 'Pending' ? 'start' : 'continue';
}

export const STEP_LABEL: Record<PlanStep, string> = {
  finish: 'Erledigt melden',
  claim: 'Übernehmen & starten',
  start: 'Starten',
  continue: 'Weiter',
};

export type PlanTone = 'overdue' | 'free' | 'running' | 'mine' | 'done' | 'issue' | 'other';

export const PLAN_PILL =
  'rounded-pill inline-flex h-6 flex-none items-center px-2.5 text-[11px] font-bold';

export const PLAN_TONE: Record<PlanTone, string> = {
  overdue: 'bg-sealwax-tint text-sealwax',
  free: 'bg-forest-soft text-forest-deep',
  running: 'bg-amber-tint text-amber-ink',
  mine: 'bg-card-alt text-ink',
  done: 'bg-forest-soft text-forest-deep',
  issue: 'bg-sealwax-tint text-sealwax',
  other: 'bg-card-alt text-ink-soft',
};

/** Der Stand als Pille. Fehlt der Name zu einer Zuweisung, steht das da statt einer Erfindung. */
export function planStatus(
  task: Task,
  userId: string | undefined,
): { label: string; tone: PlanTone } {
  if (task.status === 'Issue') return { label: 'Erledigt mit Meldung', tone: 'issue' };
  if (task.status === 'Completed') return { label: 'Erledigt', tone: 'done' };
  if (task.status === 'Cancelled') return { label: 'Entfällt', tone: 'other' };
  if (task.isOverdue) return { label: 'Überfällig', tone: 'overdue' };
  if (task.canClaim) return { label: 'Frei', tone: 'free' };
  const mine = task.assignedToUserId === userId;
  const someone = task.assignedToUserName;
  if (task.status === 'InProgress') {
    if (mine) return { label: 'Läuft', tone: 'running' };
    return { label: someone ? `Läuft bei ${someone}` : 'Läuft · Zuweisung prüfen', tone: 'other' };
  }
  if (mine) return { label: 'Dir zugeteilt', tone: 'mine' };
  if (task.assignedToUserId === null) return { label: 'Nicht zugewiesen', tone: 'other' };
  return { label: someone ? `Bei ${someone}` : 'Zuweisung prüfen', tone: 'other' };
}
